import { useEffect, useMemo, useState } from 'react';

import { OptionType } from '@evinced-private/ui-common';

import { IWebSdkTest } from 'src/apps/web-sdks/types/WebSdkDashboardServiceTypes';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { transformLabelValuesToOptions } from '../../shared/helpers';
import { LabelAddition } from '../../shared/types';
import { extractAvailableLabelTypes } from '../helpers/AddLabelTestsModalHelpers';

interface UseLabelsManagementProps {
	testRows: IWebSdkTest[];
	tenantConfiguredLabels: ConfigurationLabel[];
}

interface UseLabelsManagementReturn {
	selectedLabelType: OptionType[];
	setSelectedLabelType: (labelType: OptionType[]) => void;
	selectedValue: OptionType[];
	setSelectedValue: (value: OptionType[]) => void;
	additions: LabelAddition[];
	labelTypeOptions: OptionType[];
	valueOptions: OptionType[];
	removeAddition: (index: number) => void;
	resetAdditions: () => void;
}

export const useLabelsManagement = ({
	testRows,
	tenantConfiguredLabels
}: UseLabelsManagementProps): UseLabelsManagementReturn => {
	const [selectedLabelType, setSelectedLabelType] = useState<OptionType[]>([]);
	const [selectedValue, setSelectedValue] = useState<OptionType[]>([]);
	const [additions, setAdditions] = useState<LabelAddition[]>([]);

	// Extract available label types (excluding those already on tests)
	const labelTypeOptions = useMemo((): OptionType[] => {
		return extractAvailableLabelTypes(testRows, tenantConfiguredLabels);
	}, [testRows, tenantConfiguredLabels]);

	// Transform values for selected label type
	const valueOptions = useMemo((): OptionType[] => {
		const selectedLabelTypeValue =
			selectedLabelType.length > 0 ? (selectedLabelType[0].value as string) : undefined;
		return transformLabelValuesToOptions(tenantConfiguredLabels, selectedLabelTypeValue);
	}, [tenantConfiguredLabels, selectedLabelType]);

	// Auto-add label when both selects have values
	useEffect(() => {
		if (selectedLabelType.length > 0 && selectedValue.length > 0) {
			const newAddition: LabelAddition = {
				labelType: selectedLabelType[0],
				value: selectedValue[0]
			};
			setAdditions((prevAdditions) => {
				// Check if this label type already exists
				const existingIndex = prevAdditions.findIndex(
					(addition) => addition.labelType.value === newAddition.labelType.value
				);
				if (existingIndex >= 0) {
					// Update existing label type with new value
					const updated = [...prevAdditions];
					updated[existingIndex] = newAddition;
					return updated;
				}
				// Add new addition if label type doesn't exist
				return [...prevAdditions, newAddition];
			});
			// Reset selections for next addition
			setSelectedLabelType([]);
			setSelectedValue([]);
		}
	}, [selectedLabelType, selectedValue]);

	const removeAddition = (index: number): void => {
		setAdditions((prev) => prev.filter((_, i) => i !== index));
	};

	const resetAdditions = (): void => {
		setAdditions([]);
		setSelectedLabelType([]);
		setSelectedValue([]);
	};

	return {
		selectedLabelType,
		setSelectedLabelType,
		selectedValue,
		setSelectedValue,
		additions,
		labelTypeOptions,
		valueOptions,
		removeAddition,
		resetAdditions
	};
};
